<script lang="ts" setup>
  import useFareTableExpandableRows from '@/components/fare-table/useExpandableRows'
  import {
    FARE_TABLE_CLUB_LEVEL_CLUB,
    FARE_TABLE_CLUB_LEVEL_EXECUTIVE,
    FARE_TABLE_CLUB_LEVEL_NONE,
    FARE_TABLE_CLUB_LEVEL_VIP,
    FARE_TABLE_TRAVEL_WITH
  } from '@/components/fare-table/types'
  import type {
    TFareTableFare,
    TFareTableClubLevel,
    IFareTableFiltersModel,
    TFareTableQueryValue,
    TFareTableTravelWith,
    IFareTypeGroupKey
  } from '@/components/fare-table/types'
  import type { FareTableStoryblok } from '@/storyblok/component-types-sb'

  const props = defineProps<{ blok: FareTableStoryblok }>()
  const { isDesktop } = useBTBreakpoints()
  const route = useRoute()
  const { t } = useI18n()

  const selectedFareType = ref('')
  const filters = ref<IFareTableFiltersModel>({
    fareKeys: [],
    clubLevel: FARE_TABLE_CLUB_LEVEL_NONE,
    travelWith: []
  })

  const FARE_KEYS_QUERY_KEY = 'fareKeys'
  const DEFAULT_FALLBACK_FARE_KEY = 'ECONOMY_MINI'
  const DEFAULT_FARE_KEYS_BY_TYPE: Record<string, string[]> = {
    ECONOMY: ['ECONOMY_MINI', 'ECONOMY_BASIC', 'ECONOMY_CLASSIC', 'ECONOMY_FLEX'],
    BUSINESS: ['BUSINESS_EXPERIENCE', 'BUSINESS'],
    INTERLINE: ['INTERLINE_ECONOMY', 'INTERLINE_BUSINESS']
  }
  const supportedClubLevels: Set<TFareTableClubLevel> = new Set([
    FARE_TABLE_CLUB_LEVEL_NONE,
    FARE_TABLE_CLUB_LEVEL_CLUB,
    FARE_TABLE_CLUB_LEVEL_EXECUTIVE,
    FARE_TABLE_CLUB_LEVEL_VIP
  ])
  const isTravelWithValue = (value: string): value is TFareTableTravelWith =>
    value === FARE_TABLE_TRAVEL_WITH.infant || value === FARE_TABLE_TRAVEL_WITH.child

  const uniqueValues = (items: readonly string[]) => [...new Set(items.filter(Boolean))]

  const parseQueryList = (queryValue: TFareTableQueryValue) => {
    if (!queryValue) return []
    if (Array.isArray(queryValue)) {
      return uniqueValues(
        queryValue
          .filter((item): item is string => typeof item === 'string')
          .flatMap((item) => item.split(',').map((value) => value.trim()))
      )
    }
    if (typeof queryValue !== 'string') return []
    return uniqueValues(queryValue.split(',').map((value) => value.trim()))
  }

  const fareKeyFromKebabCase = (value: string) => value.toUpperCase().replaceAll('-', '_')

  const fares = computed(() => (props.blok.fares || []) as TFareTableFare[])
  const supportedFareKeySet = computed(
    () =>
      new Set(
        fares.value
          .map((fare) => (typeof fare.key === 'string' ? fare.key.trim() : ''))
          .filter((fareKey) => fareKey.length > 0)
      )
  )

  const allServices = computed(() => {
    return props.blok.services || []
  })
  const supportedTravelWith = computed<Set<TFareTableTravelWith>>(() => {
    const keys = allServices.value
      .map((service: any) => (typeof service?.service === 'string' ? service.service.trim().toUpperCase() : ''))
      .filter((serviceKey: string): serviceKey is TFareTableTravelWith => isTravelWithValue(serviceKey))
    return new Set(keys)
  })
  const selectedTravelWithSet = computed(() => new Set(filters.value.travelWith))
  const PRAM_TRANSPORTATION_SERVICE_KEY = 'PRAM_TRANSPORTATION'
  const baseServices = computed(() => {
    return allServices.value.filter((service: any) => {
      const serviceKey = typeof service?.service === 'string' ? service.service.toUpperCase() : ''
      if (serviceKey === FARE_TABLE_TRAVEL_WITH.infant)
        return selectedTravelWithSet.value.has(FARE_TABLE_TRAVEL_WITH.infant)
      if (serviceKey === FARE_TABLE_TRAVEL_WITH.child)
        return selectedTravelWithSet.value.has(FARE_TABLE_TRAVEL_WITH.child)
      if (serviceKey === PRAM_TRANSPORTATION_SERVICE_KEY)
        return (
          selectedTravelWithSet.value.has(FARE_TABLE_TRAVEL_WITH.infant) ||
          selectedTravelWithSet.value.has(FARE_TABLE_TRAVEL_WITH.child)
        )
      return true
    })
  })

  const fareTypeGroups = computed(() =>
    fares.value.reduce(
      (groups: Record<string, TFareTableFare[]>, fare: TFareTableFare) => {
        const type = typeof fare.type === 'string' ? fare.type.trim().toUpperCase() : 'UNKNOWN'
        if (!groups[type]) groups[type] = []
        groups[type].push(fare)
        return groups
      },
      {} as Record<string, TFareTableFare[]>
    )
  )

  const fareTypeGroupKeys = computed<IFareTypeGroupKey[]>(() =>
    Object.keys(fareTypeGroups.value).map((key: string) => ({
      key,
      title: t(`fareTable.fareType.${key}`)
    }))
  )

  const defaultFareTypeKey = computed(
    () => fareTypeGroupKeys.value.find((item) => item.key === 'ECONOMY')?.key || fareTypeGroupKeys.value[0]?.key || ''
  )
  const activeFareType = computed(() => selectedFareType.value || defaultFareTypeKey.value)
  const fareFilterOptions = computed(() => {
    const individual: { label: string; value: string }[] = []
    const interline: { label: string; value: string }[] = []
    const corporate: { label: string; value: string }[] = []

    fares.value.forEach((fare) => {
      const fareKey = typeof fare.key === 'string' ? fare.key.trim() : ''
      if (!fareKey) return

      const option = {
        label: typeof fare.title === 'string' && fare.title.trim() ? fare.title : fareKey,
        value: fareKey
      }
      const fareType = typeof fare.type === 'string' ? fare.type.trim().toUpperCase() : ''

      if (fareType === 'INTERLINE') {
        interline.push(option)
        return
      }
      if (fareKey.includes('CORPORATE')) {
        corporate.push(option)
        return
      }

      individual.push(option)
    })

    return { individual, interline, corporate }
  })
  const selectedFareKeySet = computed(() => new Set(filters.value.fareKeys))
  const hasFareKeyFilters = computed(() => filters.value.fareKeys.length > 0)
  const fareKeyOrderIndex = computed(() => {
    const order = new Map<string, number>()
    fares.value.forEach((fare, index) => {
      const fareKey = typeof fare.key === 'string' ? fare.key.trim() : ''
      if (fareKey && !order.has(fareKey)) order.set(fareKey, index)
    })
    return order
  })
  const fareKeysFromQuery = computed(() => {
    const validFareKeys = uniqueValues(
      parseQueryList(route.query[FARE_KEYS_QUERY_KEY]).map(fareKeyFromKebabCase)
    ).filter((fareKey) => supportedFareKeySet.value.has(fareKey))

    return validFareKeys.sort((a, b) => fareKeyOrderIndex.value.get(a)! - fareKeyOrderIndex.value.get(b)!)
  })

  const defaultFareKeysForType = (fareType: string) => DEFAULT_FARE_KEYS_BY_TYPE[fareType] || []
  const activeDefaultFareKeySet = computed(() => new Set(defaultFareKeysForType(activeFareType.value)))
  const areSelectedFareKeysDefault = computed(() => {
    if (filters.value.fareKeys.length !== activeDefaultFareKeySet.value.size) return false
    return filters.value.fareKeys.every((fareKey) => activeDefaultFareKeySet.value.has(fareKey))
  })
  const hasAdditionalFilters = computed(
    () => filters.value.clubLevel !== FARE_TABLE_CLUB_LEVEL_NONE || filters.value.travelWith.length > 0
  )
  const showFareTypeActiveState = computed(() => areSelectedFareKeysDefault.value && !hasAdditionalFilters.value)
  const buttonGroupActiveFareType = computed(() => (showFareTypeActiveState.value ? activeFareType.value : ''))

  const visibleFares = computed(() => {
    const effectiveKeySet = hasFareKeyFilters.value ? selectedFareKeySet.value : activeDefaultFareKeySet.value
    return fares.value.filter((fare) => {
      const fareKey = typeof fare.key === 'string' ? fare.key.trim() : ''
      return !!fareKey && effectiveKeySet.has(fareKey)
    })
  })

  const services = computed(() => baseServices.value)

  const {
    findFareService,
    hasExpandedContent,
    isRowExpanded,
    toggleRowExpanded,
    expandableRowIds,
    areAllRowsExpanded,
    setAllRowsExpanded
  } = useFareTableExpandableRows({
    services,
    visibleFares
  })

  const fareColumnCount = computed(() => Math.max(visibleFares.value.length, 1))

  const syncStateFromQuery = () => {
    selectedFareType.value = defaultFareTypeKey.value

    const nextFilters: IFareTableFiltersModel = {
      fareKeys: fareKeysFromQuery.value.length ? fareKeysFromQuery.value : defaultFareKeysForType(activeFareType.value),
      clubLevel: FARE_TABLE_CLUB_LEVEL_NONE,
      travelWith: []
    }

    filters.value = nextFilters
  }

  const updateSelectedFareType = (type: string) => {
    selectedFareType.value = type
    filters.value = {
      fareKeys: defaultFareKeysForType(type),
      clubLevel: FARE_TABLE_CLUB_LEVEL_NONE,
      travelWith: []
    }
  }

  const updateFilters = (nextFilters: IFareTableFiltersModel) => {
    const normalizedFareKeys = uniqueValues(nextFilters.fareKeys).filter((fareKey) =>
      supportedFareKeySet.value.has(fareKey)
    )
    const fallbackFareKeys =
      normalizedFareKeys.length === 0 && supportedFareKeySet.value.has(DEFAULT_FALLBACK_FARE_KEY)
        ? [DEFAULT_FALLBACK_FARE_KEY]
        : normalizedFareKeys

    filters.value = {
      fareKeys: fallbackFareKeys,
      clubLevel: supportedClubLevels.has(nextFilters.clubLevel) ? nextFilters.clubLevel : FARE_TABLE_CLUB_LEVEL_NONE,
      travelWith: uniqueValues(nextFilters.travelWith).filter(
        (value): value is TFareTableTravelWith => isTravelWithValue(value) && supportedTravelWith.value.has(value)
      )
    }
  }

  watch(
    [() => fares.value, () => route.query[FARE_KEYS_QUERY_KEY]],
    () => {
      syncStateFromQuery()
    },
    { immediate: true }
  )
</script>

<template>
  <section
    class="flex flex-col gap-2"
    :aria-label="$t('fareTable.filters.title')"
  >
    <div
      v-if="!isDesktop"
      class="flex w-full items-center justify-between gap-3 sm:justify-start"
    >
      <FareTableFilters
        :model-value="filters"
        :fare-options="fareFilterOptions"
        :default-fare-keys="defaultFareKeysForType(activeFareType)"
        @update:model-value="updateFilters"
      />

      <FareTableExpandAllSwitch
        :has-expandable-rows="expandableRowIds.length > 0"
        :model-value="areAllRowsExpanded"
        inline
        @update:model-value="setAllRowsExpanded"
      />
    </div>
    <div
      v-if="!isDesktop"
      class="mb-4"
    >
      <div
        v-if="fareTypeGroupKeys.length > 1"
        class="flex w-full gap-2 overflow-x-auto"
      >
        <ButtonGroup
          :items="fareTypeGroupKeys"
          :active="buttonGroupActiveFareType"
          @click="updateSelectedFareType"
        />
      </div>
    </div>

    <div
      v-else
      class="mb-4 grid grid-cols-[minmax(0,65%)_1fr] grid-rows-1 items-center gap-4"
    >
      <div
        v-if="fareTypeGroupKeys.length > 1"
        class="col-start-1 w-full justify-self-center"
      >
        <ButtonGroup
          :items="fareTypeGroupKeys"
          :active="buttonGroupActiveFareType"
          class="w-full"
          @click="updateSelectedFareType"
        />
      </div>
      <div class="col-start-3 justify-self-end">
        <FareTableFilters
          :model-value="filters"
          :fare-options="fareFilterOptions"
          :default-fare-keys="defaultFareKeysForType(activeFareType)"
          @update:model-value="updateFilters"
        />
      </div>
    </div>

    <div class="w-full">
      <div
        class="md:grid md:w-full md:min-w-0 md:[grid-template-columns:348px_repeat(var(--fare-columns),minmax(0,1fr))]"
        :style="{ '--fare-columns': fareColumnCount }"
      >
        <FareTableContent
          :services="services"
          :visible-fares="visibleFares"
          :club-logo="blok.clubLogo"
          :club-level="filters.clubLevel"
          :find-fare-service="findFareService"
          :has-expanded-content="hasExpandedContent"
          :is-row-expanded="isRowExpanded"
          :toggle-row-expanded="toggleRowExpanded"
        />
      </div>
    </div>
  </section>
</template>
